/**
 * Reusable interface primitives. All are keyboard operable, expose accessible names and states,
 * meet the 40–44 px touch target, and style only through tokens (see docs/style-guide.md).
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './icons';

export function IconButton({
  icon,
  label,
  onClick,
  pressed,
  size = 'md',
  variant = 'ghost',
  disabled,
  buttonRef,
  controls,
  expanded,
  hasPopup,
  className = '',
}: {
  icon: IconName;
  label: string;
  onClick?: () => void;
  pressed?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'ghost' | 'solid';
  disabled?: boolean;
  buttonRef?: RefObject<HTMLButtonElement | null>;
  controls?: string;
  expanded?: boolean;
  hasPopup?: 'dialog' | 'menu' | boolean;
  className?: string;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className={`gbr-iconbtn gbr-iconbtn--${size} gbr-iconbtn--${variant} ${className}`}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      aria-controls={controls}
      aria-expanded={expanded}
      aria-haspopup={hasPopup}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} size={size === 'lg' ? 22 : size === 'sm' ? 16 : 20} />
    </button>
  );
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  icon,
  iconAfter,
  className = '',
  ...rest
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'quiet' | 'chip';
  icon?: IconName;
  iconAfter?: IconName;
  className?: string;
  'aria-pressed'?: boolean;
  'aria-label'?: string;
  disabled?: boolean;
}) {
  return (
    <button type="button" className={`gbr-btn gbr-btn--${variant} ${className}`} onClick={onClick} {...rest}>
      {icon && <Icon name={icon} size={18} />}
      <span>{children}</span>
      {iconAfter && <Icon name={iconAfter} size={18} />}
    </button>
  );
}

export function Slider({ label, value, onChange, min = 0, max = 1, step = 0.01, disabled, format }: { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; disabled?: boolean; format?: (v: number) => string }) {
  const id = useId();
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="gbr-slider">
      <label htmlFor={id} className="gbr-visually-hidden">
        {label}
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-valuetext={format ? format(value) : `${Math.round(pct)} percent`}
        style={{ ['--gbr-fill' as string]: `${pct}%` }}
        onChange={(e) => onChange(Number(e.currentTarget.value))}
      />
    </div>
  );
}

export function Toggle({ label, checked, onChange, description }: { label: string; checked: boolean; onChange: (v: boolean) => void; description?: string }) {
  const id = useId();
  return (
    <div className="gbr-toggle-row">
      <div className="gbr-toggle-text">
        <span id={`${id}-l`}>{label}</span>
        {description && (
          <span id={`${id}-d`} className="gbr-hint">
            {description}
          </span>
        )}
      </div>
      <button type="button" role="switch" aria-checked={checked} aria-labelledby={`${id}-l`} aria-describedby={description ? `${id}-d` : undefined} className="gbr-switch" onClick={() => onChange(!checked)}>
        <span className="gbr-switch-thumb" />
      </button>
    </div>
  );
}

export function Segmented<T extends string>({ label, value, options, onChange, compact, wrap }: { label: string; value: T; options: Array<{ value: T; label: string; icon?: IconName }>; onChange: (v: T) => void; compact?: boolean; wrap?: boolean }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const idx = options.findIndex((o) => o.value === value);
  const move = (d: number) => {
    const n = (idx + d + options.length) % options.length;
    onChange(options[n].value);
    refs.current[n]?.focus();
  };
  return (
    <div role="radiogroup" aria-label={label} className={`gbr-seg ${compact ? 'gbr-seg--compact' : ''} ${wrap ? 'gbr-seg--wrap' : ''}`}>
      {options.map((o, i) => (
        <button
          key={o.value}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          className="gbr-seg-opt"
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
              e.preventDefault();
              move(1);
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
              e.preventDefault();
              move(-1);
            }
          }}
        >
          {o.icon && <Icon name={o.icon} size={16} />}
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Anchored popover with outside-click and Escape dismissal; focus moves in and returns. */
export function Popover({ open, onClose, anchor, children, label, id, placement = 'top' }: { open: boolean; onClose: () => void; anchor: RefObject<HTMLElement | null>; children: ReactNode; label: string; id: string; placement?: 'top' | 'top-end' }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const place = () => {
      const r = anchor.current!.getBoundingClientRect();
      const width = ref.current?.offsetWidth ?? 280;
      const margin = 12;
      let left = placement === 'top-end' ? r.right - width : r.left + r.width / 2 - width / 2;
      left = Math.max(margin, Math.min(window.innerWidth - width - margin, left));
      setPos({ left, bottom: window.innerHeight - r.top + 10 });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, anchor, placement]);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])');
    first?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor.current?.contains(t)) onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDown, true);
      if (prev && document.contains(prev)) prev.focus({ preventScroll: true });
    };
  }, [open, onClose, anchor]);
  if (!open) return null;
  return createPortal(
    <div ref={ref} id={id} role="dialog" aria-label={label} className="gbr-popover" style={pos ? { left: pos.left, bottom: pos.bottom } : { visibility: 'hidden' }}>
      {children}
    </div>,
    document.body,
  );
}

/** Side sheet (modal dialog) with a focus trap. */
export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('[data-autofocus], button')?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'Tab' && ref.current) {
        const items = [...ref.current.querySelectorAll<HTMLElement>('button, [href], input, select, [tabindex]:not([tabindex="-1"])')].filter((el) => !el.hasAttribute('disabled'));
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (prev && document.contains(prev)) prev.focus({ preventScroll: true });
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="gbr-sheet-backdrop" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className="gbr-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="gbr-sheet-head">
          <h2 id={titleId}>{title}</h2>
          <IconButton icon="close" label="Close" onClick={onClose} />
        </header>
        <div className="gbr-sheet-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export function Toast({ message, onDone }: { message: string | null; onDone: () => void }) {
  useEffect(() => {
    if (!message) return;
    // Long enough to read: about 60 ms a character, at least 3.2 s.
    const t = window.setTimeout(onDone, Math.max(3200, message.length * 60));
    return () => window.clearTimeout(t);
  }, [message, onDone]);
  return (
    <div className="gbr-toast-region" role="status" aria-live="polite">
      {message && <div className="gbr-toast">{message}</div>}
    </div>
  );
}

export function Section({ title, children, hint }: { title: string; children: ReactNode; hint?: string }) {
  return (
    <section className="gbr-section">
      <h3>{title}</h3>
      {hint && <p className="gbr-hint">{hint}</p>}
      {children}
    </section>
  );
}
